import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
`;

export const PageSection = styled.div`
  display: block;
`;

export const Notification = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  border-radius: 10px;
  color: #ffffff;
  font-weight: bold;
  z-index: 3000;
  transform: ${(props) => (props.$show ? "translateX(0)" : "translateX(300%)")};
  transition: transform 0.3s ease-in-out;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.success {
    background: #27ae60;
  }

  &.error {
    background: #e74c3c;
  }

  &.warning {
    background: #f39c12;
  }

  &.info {
    background: #3498db;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid #444;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35, #e55a2b);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(255, 107, 53, 0.2);
  }
`;

export const StatIcon = styled.div`
  font-size: 2.5rem;
  color: #ff6b35;
  margin-bottom: 1rem;
  display: flex;
  animation: ${float} 3s ease-in-out infinite;
  align-items: center;
`;

export const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

export const StatLabel = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  margin-top: ${(props) => (props.$eur ? "-10px" : "0px")};
`;
export const CenteredContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
export const StatChange = styled.div`
  font-size: 0.8rem;
  margin-top: 0.5rem;
  color: ${(props) => (props.$positive ? "#27ae60" : "#e74c3c")};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 600;
`;

export const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 1350px) {
    grid-template-columns: 1.5fr 1fr;
  }
  @media (max-width: 1150px) {
    grid-template-columns: 1fr;
  }
`;

export const ChartContainer = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid #444;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff6b35;
    transform: translateY(-2px);
  }
`;

export const ChartTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  margin: 0;
`;

export const ChartWrapper = styled.div`
  margin-top: 1rem;
  position: relative;

  .recharts-tooltip-wrapper {
    z-index: 1000;
  }

  .recharts-legend-wrapper {
    margin-top: 1rem;
  }
`;

export const TableContainer = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid #444;
  overflow-x: auto;
  transition: all 0.3s ease;

  &:hover {
    border-color: #ff6b35;
  }
`;

export const TableActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const ViewAllButton = styled.button`
  background: #ff6b35;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  font-size: 0.9rem;

  &:hover {
    background: #e55a2b;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHeader = styled.th`
  padding: 1rem;
  text-align: center;
  border-bottom: 2px solid #444;
  color: #ff6b35;
  font-weight: bold;
  background: rgba(255, 107, 53, 0.1);
  font-size: 0.9rem;

  &:last-child {
    text-align: right;
  }

  &:first-child {
    text-align: left;
  }
`;

export const TableCell = styled.td`
  padding: 1rem;
  text-align: center;
  border-bottom: 1px solid #333;
  color: #cccccc;
  font-size: 0.9rem;

  &:first-child {
    text-align: left;
    font-weight: 500;
  }

  &:last-child {
    text-align: right;
  }
`;

export const TableRow = styled.tr`
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 53, 0.05);
    transform: translateX(2px);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

export const StatusOption = styled.button`
  padding: 0.4rem 1rem;
  border-radius: 8px;
  border: 2px solid transparent;
  font-weight: bold;
  transition: all 0.3s ease;
  text-align: left;
  margin-left: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  color: ${(props) => {
    switch (props.$status) {
      case "delivered":
      case "active":
        return "#27ae60";
      case "processing":
        return "#f39c12";
      case "inactive":
      case "canceled":
        return "#e74c3c";
      case "shipped":
        return "#3498db";
      case "pending":
        return "#f39c12";
      default:
        return "#cccccc";
    }
  }};
  background: ${(props) => {
    switch (props.$status) {
      case "delivered":
      case "active":
        return "rgba(39, 174, 96, 0.1)";
      case "processing":
      case "pending":
        return "rgba(243, 156, 18, 0.1)";
      case "inactive":
      case "canceled":
        return "rgba(231, 76, 60, 0.1)";
      case "shipped":
        return "rgba(52, 152, 219, 0.1)";
      default:
        return "rgba(204, 204, 204, 0.1)";
    }
  }};

  border-color: ${(props) => {
    switch (props.$status) {
      case "delivered":
      case "active":
        return "#27ae60";
      case "processing":
      case "pending":
        return "#f39c12";
      case "inactive":
      case "canceled":
        return "#e74c3c";
      case "shipped":
        return "#3498db";
      default:
        return "#cccccc";
    }
  }};
`;

// Loading state components
export const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #ff6b35;
  font-size: 1.1rem;
  gap: 1rem;
`;

export const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  color: #cccccc;
  font-size: 1.1rem;
`;

// Chart lock overlay components
export const ChartLockOverlay = styled.div`
  position: relative;
  height: 300px;
`;

export const ChartBlur = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(8px);
  background: rgba(0, 0, 0, 0.25);
  border-radius: 10px;
  z-index: 2;
`;

export const ChartLockIcon = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: #ff6b35;

  svg {
    font-size: 3rem;
    animation: ${float} 2s ease-in-out infinite;
  }

  .lock-text {
    font-size: 1.1rem;
    font-weight: 600;
    text-align: center;
    color: #ffffff;

    .highlight {
      color: #ff6b35;
      font-weight: bold;
    }
  }
`;

export const ChartBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 10px;
  z-index: 1;
`;

// Band selector components
export const BandSelectorContainer = styled.div`
  margin-bottom: 2rem;
`;

export const BandSelectorTitle = styled.h3`
  color: #ff6b35;
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

export const BandSliderWrapper = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 15px;
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border: 1px solid #444;
`;

export const BandGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  padding: 1.5rem;
  transition: transform 0.3s ease;
  transform: translateX(${(props) => props.$offset}px);
`;

export const BandCard = styled.div`
  background: linear-gradient(135deg, #2c2c2c 0%, #3a3a3a 100%);
  border-radius: 12px;
  padding: 1rem;
  border: 2px solid ${(props) => (props.$selected ? "#ff6b35" : "#555")};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: #ff6b35;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(255, 107, 53, 0.2);
  }

  ${(props) =>
    props.$selected &&
    `
    background: linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%);
    color: white;
    box-shadow: 0 8px 25px rgba(255, 107, 53, 0.3);
  `}
`;

export const BandName = styled.h4`
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: ${(props) => (props.$selected ? "white" : "#ff6b35")};
`;

export const BandGenre = styled.p`
  font-size: 0.8rem;
  color: ${(props) => (props.$selected ? "rgba(255,255,255,0.8)" : "#cccccc")};
  margin-bottom: 0.5rem;
`;

export const BandProducts = styled.div`
  font-size: 0.7rem;
  color: ${(props) => (props.$selected ? "rgba(255,255,255,0.9)" : "#999")};
  font-weight: 500;
`;

export const SliderNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(props) => (props.$direction === "left" ? "left: 10px;" : "right: 10px;")}

  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 107, 53, 0.9);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  pointer-events: ${(props) => (props.$visible ? "auto" : "none")};

  &:hover {
    background: rgba(255, 107, 53, 1);
    transform: translateY(-50%) scale(1.1);
  }

  svg {
    font-size: 1.2rem;
  }
`;

export const BandSelector = styled.div`
  margin-bottom: 2rem;
`;

export const LowStockAlert = styled.div`
  background: linear-gradient(135deg, #ff475710 0%, #ff383810 100%);
  border: 2px solid rgba(255, 71, 87, 0.3);
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  animation: ${fadeIn} 0.5s ease-out;
  box-shadow: 0 8px 25px rgba(255, 71, 87, 0.3);
`;

export const AlertHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

export const AlertIcon = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${float} 2s ease-in-out infinite;

  svg {
    font-size: 1.5rem;
    color: #ffffff;
  }
`;

export const AlertTitle = styled.h3`
  color: #ffffff;
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

export const AlertContent = styled.div`
  margin-bottom: 1rem;
`;

export const ProductAlert = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 1rem;
  margin-bottom: 0.8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ProductName = styled.span`
  color: #ffffff;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

export const StockCount = styled.span`
  background: rgba(255, 255, 255, 0.9);
  color: #ff4757;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-weight: bold;
  font-size: 0.9rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

export const AlertActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

export const AlertButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &.primary {
    background: #ffffff;
    color: #ff4757;
    border-color: #ffffff;

    &:hover {
      background: #f8f9fa;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
    }
  }
`;
